import { useId, useMemo, useRef, useState } from 'react'
import type { AutofillTarget, FormField, FormSchema } from '@kfolks/types'
import { FormFieldType } from '@kfolks/types'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/services/api.client'
import { AutocompleteField } from './AutocompleteField'
import { shouldSkipByCondition } from './conditional'
import type { ProviderAccessEntry } from './ProviderAccessListField'
import { ProviderAccessListField } from './ProviderAccessListField'
import { validateFormData } from './validate'
import { FieldWidget } from './widgets'

export interface DynamicFormRendererProps {
	formSchema: FormSchema
	value: Record<string, unknown>
	onChange: (next: Record<string, unknown>) => void
	/**
	 * Optional sibling stream of human-readable labels for fields whose
	 * `value` is an opaque id (autocomplete / data-source / user-picker).
	 * Hosts that submit the form bundle these into the request payload as
	 * `formDataLabels`; the backend persists the snapshot under
	 * `formData.__labels` so the row summary stays audit-stable when a
	 * provider is renamed downstream.
	 *
	 * Single-select: label string. Multi-select: comma-joined labels.
	 * Cleared values emit `undefined` (host should drop the entry).
	 */
	onLabelsChange?: (next: Record<string, string>) => void
	onSubmit?: (next: Record<string, unknown>) => void
	disabled?: boolean
	submitting?: boolean
	submitLabel?: string
}

/**
 * Renders a request type's `formSchema` field-by-field. No JSON-Schema
 * intermediate, no third-party form library — every field type maps to a
 * typed React widget below. Validation is the same set of rules the backend
 * runs at submit-time (`validateFormData`); errors are shown inline next to
 * each field and the submit button stays disabled until the form is valid
 * AFTER the user has interacted with it (so the empty initial state doesn't
 * scream errors at the user).
 *
 * Accessibility: every widget uses `htmlFor`+`id` linkage, marks required
 * with `aria-required`, and points `aria-describedby` at the field's error
 * (when present) and description (when present). Errors carry `role="alert"`
 * so screen readers announce them as they appear.
 */
export function DynamicFormRenderer({
	formSchema,
	value,
	onChange,
	onLabelsChange,
	onSubmit,
	disabled,
	submitting,
	submitLabel = 'Submit',
}: DynamicFormRendererProps) {
	const { visibleFields, validation, submitAttempted, markTouched, attemptSubmit, showError } = useFormState(
		value,
		formSchema,
	)

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const ok = attemptSubmit()
		if (ok && onSubmit) onSubmit(value)
	}

	const makeFieldChangeHandler = (field: FormField) => (next: unknown) => {
		void applyFieldChangeWithAutofill(field, next, value, onChange)
	}

	// Accumulator for the autocomplete-emitted label snapshots. The renderer
	// is the only thing that knows the picked label per field at the moment
	// the user makes the choice; host components consume the merged map via
	// `onLabelsChange`. The ref mirrors the latest map so we can compute the
	// next snapshot in the event handler — calling the parent's setter from
	// inside a `setLabels` updater would schedule a parent setState during
	// React's child-state computation and trip the "Cannot update a component
	// while rendering a different component" warning.
	const labelsRef = useRef<Record<string, string>>({})
	const [, setLabels] = useState<Record<string, string>>({})
	const makeLabelChangeHandler = (field: FormField) => (next: string | string[] | undefined) => {
		const out = { ...labelsRef.current }
		if (next === undefined || (Array.isArray(next) && next.length === 0)) {
			delete out[field.id]
		} else {
			out[field.id] = Array.isArray(next) ? next.join(', ') : next
		}
		labelsRef.current = out
		setLabels(out)
		onLabelsChange?.(out)
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4" noValidate>
			{visibleFields.map((field) => (
				<FieldRow
					key={field.id}
					field={field}
					value={value[field.id]}
					error={showError(field.id)}
					disabled={disabled}
					onChange={makeFieldChangeHandler(field)}
					onLabelChange={makeLabelChangeHandler(field)}
					onBlur={() => markTouched(field.id)}
				/>
			))}

			{onSubmit ? (
				<div className="flex justify-end pt-2">
					<Button type="submit" disabled={disabled || submitting || (submitAttempted && !validation.valid)}>
						{submitting ? 'Submitting…' : submitLabel}
					</Button>
				</div>
			) : null}
		</form>
	)
}

function useFormState(value: Record<string, unknown>, formSchema: FormSchema) {
	const [touched, setTouched] = useState<Record<string, boolean>>({})
	const [submitAttempted, setSubmitAttempted] = useState(false)

	const visibleFields = useMemo(
		() => formSchema.fields.filter((f) => !shouldSkipByCondition(f, value)),
		[formSchema.fields, value],
	)
	const validation = useMemo(() => validateFormData(value, formSchema), [value, formSchema])

	const markTouched = (fieldId: string) => setTouched((prev) => ({ ...prev, [fieldId]: true }))

	const attemptSubmit = (): boolean => {
		setSubmitAttempted(true)
		const allTouched: Record<string, boolean> = {}
		for (const f of visibleFields) allTouched[f.id] = true
		setTouched(allTouched)
		return validation.valid
	}

	const showError = (fieldId: string): string | null => {
		const message = validation.errorsByField[fieldId]
		if (!message) return null
		if (!touched[fieldId] && !submitAttempted) return null
		return message
	}

	return { visibleFields, validation, touched, submitAttempted, markTouched, attemptSubmit, showError }
}

// ─── Field row ───────────────────────────────────────────────────────────────

interface FieldRowProps {
	field: FormField
	value: unknown
	error: string | null
	disabled?: boolean
	onChange: (next: unknown) => void
	onLabelChange: (next: string | string[] | undefined) => void
	onBlur: () => void
}

function FieldRow({ field, value, error, disabled, onChange, onLabelChange, onBlur }: FieldRowProps) {
	const id = useId()
	const errorId = `${id}-error`
	const descId = `${id}-desc`
	const describedBy = [error ? errorId : null, field.description ? descId : null].filter(Boolean).join(' ') || undefined

	if (isAutocompleteFamily(field.type)) {
		return (
			<AutocompleteRow
				field={field}
				value={value}
				disabled={disabled}
				error={error}
				errorId={errorId}
				onChange={onChange}
				onLabelChange={onLabelChange}
				onBlur={onBlur}
			/>
		)
	}
	if (field.type === FormFieldType.PROVIDER_ACCESS_LIST) {
		const arr = Array.isArray(value) ? (value as ProviderAccessEntry[]) : []
		return (
			<ProviderAccessListField
				label={field.label}
				required={field.required}
				description={field.description}
				disabled={disabled}
				withDuration={field.withDuration === true}
				value={arr}
				onChange={onChange}
			/>
		)
	}
	if (field.type === FormFieldType.FILE || field.type === FormFieldType.FILE_MULTI) {
		return <FieldUnsupportedNotice label={field.label} reason="File upload widget is still being wired." />
	}
	return (
		<StandardFieldRow
			id={id}
			field={field}
			value={value}
			disabled={disabled}
			error={error}
			errorId={errorId}
			descId={descId}
			describedBy={describedBy}
			onChange={onChange}
			onBlur={onBlur}
		/>
	)
}

function AutocompleteRow({
	field,
	value,
	disabled,
	error,
	errorId,
	onChange,
	onLabelChange,
	onBlur,
}: {
	field: FormField
	value: unknown
	disabled?: boolean
	error: string | null
	errorId: string
	onChange: (next: unknown) => void
	onLabelChange: (next: string | string[] | undefined) => void
	onBlur: () => void
}) {
	const isMulti = isMultiAutocomplete(field)
	const dataSourceKey = field.dataSource ?? defaultDataSourceFor(field.type)
	if (!dataSourceKey) {
		return <FieldUnsupportedNotice label={field.label} reason={`No dataSource set for "${field.label}"`} />
	}
	const shared = {
		dataSourceKey,
		label: field.label,
		required: field.required,
		disabled,
		description: field.description,
	}
	return (
		<div onBlur={onBlur}>
			{isMulti ? (
				<AutocompleteField
					{...shared}
					multi
					value={Array.isArray(value) ? (value as string[]) : []}
					onChange={onChange}
					onLabelChange={onLabelChange}
				/>
			) : (
				<AutocompleteField
					{...shared}
					value={typeof value === 'string' ? value : undefined}
					onChange={onChange}
					onLabelChange={(label) => onLabelChange(label)}
				/>
			)}
			{error ? <FieldError id={errorId} message={error} /> : null}
		</div>
	)
}

function isMultiAutocomplete(field: FormField): boolean {
	return (
		field.type === FormFieldType.AUTOCOMPLETE_MULTI ||
		field.type === FormFieldType.USER_PICKER_MULTI ||
		(field.type === FormFieldType.AUTOCOMPLETE && field.multiSelect === true)
	)
}

function StandardFieldRow({
	id,
	field,
	value,
	disabled,
	error,
	errorId,
	descId,
	describedBy,
	onChange,
	onBlur,
}: {
	id: string
	field: FormField
	value: unknown
	disabled?: boolean
	error: string | null
	errorId: string
	descId: string
	describedBy: string | undefined
	onChange: (next: unknown) => void
	onBlur: () => void
}) {
	return (
		<div className="space-y-1">
			<label htmlFor={id} className="text-sm font-medium leading-none text-foreground">
				{field.label}
				{field.required ? (
					<span aria-hidden className="ml-0.5 text-destructive">
						*
					</span>
				) : null}
			</label>
			<FieldWidget
				id={id}
				field={field}
				value={value}
				disabled={disabled}
				ariaRequired={field.required ?? false}
				ariaInvalid={Boolean(error)}
				ariaDescribedBy={describedBy}
				onChange={onChange}
				onBlur={onBlur}
			/>
			{field.description ? (
				<p id={descId} className="text-xs text-muted-foreground">
					{field.description}
				</p>
			) : null}
			{error ? <FieldError id={errorId} message={error} /> : null}
		</div>
	)
}

// ─── Errors / unsupported notice ─────────────────────────────────────────────

function FieldError({ id, message }: { id: string; message: string }) {
	return (
		<p id={id} role="alert" className="text-xs text-destructive">
			{message}
		</p>
	)
}

function FieldUnsupportedNotice({ label, reason }: { label: string; reason: string }) {
	return (
		<div className="rounded-md border border-dashed border-input bg-muted/40 p-3 text-xs text-muted-foreground">
			<span className="font-medium text-foreground">{label}</span>: {reason}
		</div>
	)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isAutocompleteFamily(type: FormFieldType): boolean {
	return (
		type === FormFieldType.AUTOCOMPLETE ||
		type === FormFieldType.AUTOCOMPLETE_MULTI ||
		type === FormFieldType.USER_PICKER ||
		type === FormFieldType.USER_PICKER_MULTI ||
		type === FormFieldType.PROJECT_PICKER ||
		type === FormFieldType.COUNTRY_PICKER
	)
}

function defaultDataSourceFor(type: FormFieldType): string | null {
	switch (type) {
		case FormFieldType.USER_PICKER:
		case FormFieldType.USER_PICKER_MULTI:
			return 'employees.active'
		case FormFieldType.PROJECT_PICKER:
			return 'projects.active'
		case FormFieldType.COUNTRY_PICKER:
			return 'countries.all'
		default:
			return null
	}
}

/**
 * Write the picked value and (if the field declares autofillTargets) resolve
 * and layer prefill values on top in a single onChange call. A second
 * onChange would race the React batching window.
 */
async function applyFieldChangeWithAutofill(
	field: FormField,
	next: unknown,
	current: Record<string, unknown>,
	onChange: (next: Record<string, unknown>) => void,
): Promise<void> {
	const base = { ...current, [field.id]: next }
	const autofill = field.autofillTargets
	if (!autofill || autofill.length === 0 || typeof next !== 'string' || !next) {
		onChange(base)
		return
	}
	const resolved = await resolveAutofillTargets(autofill, next)
	onChange({ ...base, ...resolved })
}

/**
 * Run schema-declared autofills after a user_picker selection. Single source
 * supported: `employee` — fetches `/employees/:id` and copies the named field
 * into the target. Silently no-ops on fetch failure so a stale id doesn't
 * break the user's flow.
 */
async function resolveAutofillTargets(targets: AutofillTarget[], pickedId: string): Promise<Record<string, unknown>> {
	const result: Record<string, unknown> = {}
	const employeeTargets = targets.filter((t) => t.source === 'employee')
	if (employeeTargets.length === 0) return result

	try {
		const res = await apiClient.get<Record<string, unknown>>(`/employees/${pickedId}`)
		const employee = res.data
		for (const t of employeeTargets) {
			const resolved = employee[t.field]
			if (resolved !== undefined && resolved !== null) result[t.target] = resolved
		}
	} catch {
		// Autofill is best-effort — a failed lookup shouldn't break the form.
	}
	return result
}
